/**
 * Effects Panel Component for Reels video editor
 * Video filters, color adjustments, and effects
 */

import React, { useState, useCallback } from 'react';
import { Wand2, SunMedium, Contrast, Droplets, Thermometer, Sparkles, RotateCcw } from 'lucide-react';
import { useProject, useUI } from '../stores/projectStore';
import { VIDEO_FILTERS, COLOR_ADJUSTMENTS, VIDEO_EFFECTS, TRANSITIONS } from '../utils/effectPresets';

const EffectsPanel = () => {
  const { project, actions } = useProject();
  const { ui } = useUI();
  const [activeTab, setActiveTab] = useState('filters'); // 'filters' | 'adjust' | 'effects' | 'transitions'

  // Get selected clip and its track
  const getSelectedClipInfo = () => {
    for (const track of project.tracks) {
      const clip = track.clips.find(c => c.id === ui.selectedClipId);
      if (clip) {
        return { track, clip };
      }
    }
    return { track: null, clip: null };
  };

  const { track, clip } = getSelectedClipInfo();

  // Apply filter to selected clip
  const applyFilter = useCallback((filter) => {
    if (!clip || !track) return;

    const existingFilterIdx = (clip.effects || []).findIndex(e => e.type === 'filter');
    const newEffect = {
      id: `effect-${Date.now()}`,
      type: 'filter',
      filterId: filter.id,
      filter: filter.filter,
    };

    if (existingFilterIdx >= 0) {
      actions.updateEffect(track.id, clip.id, clip.effects[existingFilterIdx].id, newEffect);
    } else {
      actions.addEffect(track.id, clip.id, newEffect);
    }
  }, [clip, track, actions]);

  // Get current filter for clip
  const getCurrentFilter = () => {
    if (!clip?.effects) return 'none';
    const filterEffect = clip.effects.find(e => e.type === 'filter');
    return filterEffect?.filterId || 'none';
  };

  // Update color adjustment
  const updateColorAdjustment = useCallback((property, value) => {
    if (!clip || !track) return;

    const existingAdjustIdx = (clip.effects || []).findIndex(e => e.type === 'colorAdjust');

    if (existingAdjustIdx >= 0) {
      const existing = clip.effects[existingAdjustIdx];
      const newAdjustments = { ...existing.adjustments, [property]: value };
      actions.updateEffect(track.id, clip.id, existing.id, {
        ...existing,
        adjustments: newAdjustments,
      });
    } else {
      const newEffect = {
        id: `effect-${Date.now()}`,
        type: 'colorAdjust',
        adjustments: { [property]: value },
      };
      actions.addEffect(track.id, clip.id, newEffect);
    }
  }, [clip, track, actions]);

  // Get current color adjustments
  const getColorAdjustments = () => {
    if (!clip?.effects) return {};
    const adjustEffect = clip.effects.find(e => e.type === 'colorAdjust');
    return adjustEffect?.adjustments || {};
  };

  // Reset all adjustments
  const resetAdjustments = useCallback(() => {
    if (!clip || !track) return;
    const adjustEffect = clip.effects?.find(e => e.type === 'colorAdjust');
    if (adjustEffect) {
      actions.removeEffect(track.id, clip.id, adjustEffect.id);
    }
  }, [clip, track, actions]);

  // Apply video effect
  const applyVideoEffect = useCallback((effect) => {
    if (!clip || !track) return;

    const existingIdx = (clip.effects || []).findIndex(e => e.type === 'videoEffect');

    if (effect.id === 'none') {
      if (existingIdx >= 0) {
        actions.removeEffect(track.id, clip.id, clip.effects[existingIdx].id);
      }
      return;
    }

    const newEffect = {
      id: `effect-${Date.now()}`,
      type: 'videoEffect',
      effectId: effect.id,
      ...effect,
    };

    if (existingIdx >= 0) {
      actions.updateEffect(track.id, clip.id, clip.effects[existingIdx].id, newEffect);
    } else {
      actions.addEffect(track.id, clip.id, newEffect);
    }
  }, [clip, track, actions]);

  const currentAdjustments = getColorAdjustments();
  const currentFilter = getCurrentFilter();

  if (!clip) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-4 text-center">
        <Wand2 className="w-12 h-12 text-slate-700 mb-3" />
        <p className="text-slate-400 text-sm">Select a clip to apply effects</p>
        <p className="text-slate-600 text-xs mt-1">Click on a clip in the timeline</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-slate-800">
        <h3 className="text-sm font-semibold text-white mb-1">Effects & Filters</h3>
        <p className="text-xs text-slate-500 truncate">Editing: {clip.name || 'Selected Clip'}</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800">
        {[
          { id: 'filters', label: 'Filters' },
          { id: 'adjust', label: 'Adjust' },
          { id: 'effects', label: 'Effects' },
          { id: 'transitions', label: 'Transitions' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${activeTab === tab.id ? 'text-teal-300 border-b-2 border-teal-400' : 'text-slate-400 hover:text-white'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto p-4">
        {activeTab === 'filters' && (
          <div className="grid grid-cols-3 gap-2">
            {VIDEO_FILTERS.map((filter) => (
              <button
                key={filter.id}
                onClick={() => applyFilter(filter)}
                className={`p-2 rounded-lg text-center transition-all ${currentFilter === filter.id ? 'bg-teal-600 ring-2 ring-teal-400' : 'bg-slate-800 hover:bg-slate-700'}`}
              >
                <div
                  className="w-full aspect-square rounded bg-gradient-to-br from-teal-500 to-violet-600 mb-1"
                  style={{ filter: filter.filter || 'none' }}
                />
                <span className="text-xs text-white">{filter.name}</span>
              </button>
            ))}
          </div>
        )}

        {activeTab === 'adjust' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={resetAdjustments}
                className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-white hover:bg-slate-800 rounded"
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </button>
            </div>

            {/* Brightness */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <SunMedium className="w-4 h-4" />
                  Brightness
                </label>
                <span className="text-xs text-slate-500">
                  {currentAdjustments.brightness || COLOR_ADJUSTMENTS.brightness.default}%
                </span>
              </div>
              <input
                type="range"
                min={COLOR_ADJUSTMENTS.brightness.min}
                max={COLOR_ADJUSTMENTS.brightness.max}
                value={currentAdjustments.brightness || COLOR_ADJUSTMENTS.brightness.default}
                onChange={(e) => updateColorAdjustment('brightness', Number(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Contrast */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <Contrast className="w-4 h-4" />
                  Contrast
                </label>
                <span className="text-xs text-slate-500">
                  {currentAdjustments.contrast || COLOR_ADJUSTMENTS.contrast.default}%
                </span>
              </div>
              <input
                type="range"
                min={COLOR_ADJUSTMENTS.contrast.min}
                max={COLOR_ADJUSTMENTS.contrast.max}
                value={currentAdjustments.contrast || COLOR_ADJUSTMENTS.contrast.default}
                onChange={(e) => updateColorAdjustment('contrast', Number(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Saturation */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <Droplets className="w-4 h-4" />
                  Saturation
                </label>
                <span className="text-xs text-slate-500">
                  {currentAdjustments.saturation || COLOR_ADJUSTMENTS.saturation.default}%
                </span>
              </div>
              <input
                type="range"
                min={COLOR_ADJUSTMENTS.saturation.min}
                max={COLOR_ADJUSTMENTS.saturation.max}
                value={currentAdjustments.saturation || COLOR_ADJUSTMENTS.saturation.default}
                onChange={(e) => updateColorAdjustment('saturation', Number(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Temperature (Hue) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <Thermometer className="w-4 h-4" />
                  Temperature
                </label>
                <span className="text-xs text-slate-500">
                  {currentAdjustments.hue || COLOR_ADJUSTMENTS.hue.default}
                </span>
              </div>
              <input
                type="range"
                min={COLOR_ADJUSTMENTS.hue.min}
                max={COLOR_ADJUSTMENTS.hue.max}
                value={currentAdjustments.hue || COLOR_ADJUSTMENTS.hue.default}
                onChange={(e) => updateColorAdjustment('hue', Number(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Blur */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <Sparkles className="w-4 h-4" />
                  Blur
                </label>
                <span className="text-xs text-slate-500">
                  {currentAdjustments.blur || COLOR_ADJUSTMENTS.blur.default}px
                </span>
              </div>
              <input
                type="range"
                min={COLOR_ADJUSTMENTS.blur.min}
                max={COLOR_ADJUSTMENTS.blur.max}
                step="0.5"
                value={currentAdjustments.blur || COLOR_ADJUSTMENTS.blur.default}
                onChange={(e) => updateColorAdjustment('blur', Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        )}

        {activeTab === 'effects' && (
          <div className="space-y-4">
            {/* Motion effects */}
            <div>
              <h4 className="text-xs text-slate-400 mb-2">Motion</h4>
              <div className="grid grid-cols-2 gap-2">
                {VIDEO_EFFECTS.filter(e => e.category === 'motion' || e.category === 'none').map((effect) => (
                  <button
                    key={effect.id}
                    onClick={() => applyVideoEffect(effect)}
                    className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-white text-left transition-colors"
                  >
                    {effect.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Stylized effects */}
            <div>
              <h4 className="text-xs text-slate-400 mb-2">Stylized</h4>
              <div className="grid grid-cols-2 gap-2">
                {VIDEO_EFFECTS.filter(e => e.category === 'stylized').map((effect) => (
                  <button
                    key={effect.id}
                    onClick={() => applyVideoEffect(effect)}
                    className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-white text-left transition-colors"
                  >
                    {effect.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transitions' && (
          <div className="space-y-4">
            <p className="text-xs text-slate-500 mb-4">
              Apply transitions between clips by dragging a transition onto the junction.
            </p>

            {/* Group by category */}
            {['basic', 'fade', 'slide', 'wipe', 'zoom', 'stylized'].map((category) => {
              const categoryTransitions = TRANSITIONS.filter(t => t.category === category);
              if (categoryTransitions.length === 0) return null;

              return (
                <div key={category}>
                  <h4 className="text-xs text-slate-400 mb-2 capitalize">{category}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {categoryTransitions.map((transition) => (
                      <button
                        key={transition.id}
                        className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-white text-left transition-colors"
                      >
                        <div className="font-medium">{transition.name}</div>
                        <div className="text-slate-500 mt-0.5">{transition.duration}s</div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EffectsPanel;
